import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useAuth from '../../hooks/AuthHook.jsx';
import Shift from '../../locum/Shift.jsx';
import StorePage from './StorePage.jsx';
import useStore from '../../hooks/StoreHook.jsx';
import BackArrowButton from '../../common/BackArrowButton.jsx';
import LocumProfile from '../../locum/LocumProfile.jsx';
import { MILLISECONDS_IN_AN_HOUR } from '../../Constants.js';
import ConfirmationModal from './ConfirmationModal.jsx';
import { getShiftsForStore, postBulkShiftEdit, postBulkShiftStoreChange, postShiftStoreChange } from '../../Api.js';
import { NAVIGATION_OPTIONS } from '../BusinessConstants.js';
import BusinessShiftCard from '../common/BusinessShiftCard.jsx';
import { ReasonDropdown } from '../common/ReasonDropdown.jsx';
import { EditShiftButton, EditShiftConfirmationModal as EditShiftConfirmation } from './EditShiftButton.jsx';
import { DeleteShiftButton } from './DeleteShiftControl.jsx';
import './UpcomingShiftsPage.scss'
import { SHIFT_STATUSES } from '../../Constants.js';

const STORE_CHANGE_OPTIONS = {
  BUSINESS_REQUEST: {
    key: 'BUSINESS_REQUEST',
    text: 'Requested by business',
  },
  LOCUM_NEGOTIATED: {
    key: 'LOCUM_NEGOTIATED',
    text: 'Negotiated with Locum',
  },
  MISTAKE: {
    key: 'MISTAKE',
    text: 'Mistake with selected store',
  },
  OTHER: {
    key: 'OTHER',
    text: 'Other',
  }
};


const MAX_BULK_SHIFT_EDITS = 10;
const DEFAULT_BULK_SELECTED_FIELDS = {
  startTime: false,
  endTime: false,
  unpaidBreakTime: false,
  rate: false,
  negotiable: false,
  billToMedicare: false,
};
const BULK_ACTION_MODES = {
  EDIT_SHIFTS: 'EDIT_SHIFTS',
  CHANGE_STORE: 'CHANGE_STORE',
};

const isEditableShift = (shift) => (
  shift !== null
  && [SHIFT_STATUSES.UNFILLED.key, SHIFT_STATUSES.FILLED.key, SHIFT_STATUSES.CONFIRMED.key].includes(shift.status)
  && shift.startTime > Date.now()
);

const BulkEditShiftButton = ({ selectedShifts, clearSelection, storeId, disabled = false, buttonText = 'Bulk edit selected', setBulkStatusMessage = () => {} }) => {
  const { setShifts } = useStore();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(null);
  const [errorText, setErrorText] = useState(null);
  const [selectedFields, setSelectedFields] = useState(DEFAULT_BULK_SELECTED_FIELDS);
  const close = () => {
    setOpen(false);
    setReason(null);
    setErrorText(null);
    setSelectedFields(DEFAULT_BULK_SELECTED_FIELDS);
  };
  const confirm = (setLoading, startTimeOffset, endTimeOffset, unpaidBreakTime, rate, negotiable, billToMedicare) => {
    setLoading(true);
    const changeSet = {
    };
    if (selectedFields.startTime) {
      changeSet.startTimeOffset = startTimeOffset;
    }
    if (selectedFields.endTime) {
      changeSet.endTimeOffset = endTimeOffset;
    }
    if (selectedFields.unpaidBreakTime) {
      changeSet.unpaidBreakTime = Math.round((unpaidBreakTime / 60) * MILLISECONDS_IN_AN_HOUR);
    }
    if (selectedFields.rate) {
      changeSet.rate = Math.round(rate * 100);
    }
    if (selectedFields.negotiable) {
      changeSet.negotiable = negotiable;
    }
    if (selectedFields.billToMedicare) {
      changeSet.billToMedicare = billToMedicare;
    }
    const shiftIds = selectedShifts.map(({ _id }) => _id);
    postBulkShiftEdit(shiftIds, changeSet, reason, selectedFields)
      .then((response) => {
        if (response.error) {
          setLoading(false);
          setErrorText(response.error);
          return;
        }
        getShiftsForStore(storeId)
          .then(({ shifts }) => {
            setShifts(shifts);
            setBulkStatusMessage({ type: 'success', text: response.message || `Successfully updated ${shiftIds.length} shift${shiftIds.length === 1 ? '' : 's'}.` });
            clearSelection();
            setLoading(false);
            close();
          })
          .catch(() => {
            setBulkStatusMessage({ type: 'warning', text: 'Bulk edit succeeded, but shifts could not refresh. Please reload the page.' });
            clearSelection();
            setLoading(false);
            close();
          });
      })
      .catch((err) => {
        setLoading(false);
        setErrorText(err.message || 'An error occurred! Please report this to the administrators.');
      });
  };
  return (
    <>
      <button className={`bulk-edit-shifts very-dark ${disabled ? 'disabled' : ''}`} onClick={() => setOpen(true)} disabled={disabled}>
        {buttonText}
      </button>
      { open && selectedShifts.length > 0 &&
        <EditShiftConfirmation
          close={close}
          confirm={confirm}
          setReason={setReason}
          reason={reason}
          shift={selectedShifts[0]}
          errorText={errorText}
          bulkShiftCount={selectedShifts.length}
          selectedFields={selectedFields}
          setSelectedFields={setSelectedFields}
        />
      }
    </>
  );
};

const BulkShiftActions = ({
  selectedShiftsOrdered,
  bulkActionMode,
  startBulkActionMode,
  cancelBulkActionMode,
  storeId,
  openBulkStoreChange,
  bulkStatusMessage,
  setBulkStatusMessage,
}) => {
  const selectedShiftCount = selectedShiftsOrdered.length;
  const actionDisabled = selectedShiftCount === 0;
  const actionButtonClass = actionDisabled ? 'disabled' : '';

  if (bulkActionMode === null) {
    return (
      <section className={'bulk-shift-actions'}>
        <section className={'bulk-shift-action-text-block'}>
          <span className={'bulk-shift-selection-text'}>
            {'Choose a bulk action to filter eligible shifts and select the shifts you want to update.'}
          </span>
        </section>
        <section className={'bulk-shift-buttons'}>
          <button
            type={'button'}
            className={'bulk-edit-shifts very-dark'}
            onClick={() => startBulkActionMode(BULK_ACTION_MODES.EDIT_SHIFTS)}
          >
            {'Bulk edit shifts'}
          </button>
          <button
            type={'button'}
            className={'bulk-change-store-shifts very-dark'}
            onClick={() => startBulkActionMode(BULK_ACTION_MODES.CHANGE_STORE)}
          >
            {'Bulk change store'}
          </button>
        </section>
        {bulkStatusMessage && <section className={`bulk-shift-status ${bulkStatusMessage.type}`}>{bulkStatusMessage.text}</section>}
      </section>
    );
  }

  return (
    <section className={'bulk-shift-actions'}>
      <section className={'bulk-shift-action-text-block'}>
        <span className={'bulk-shift-selection-text'}>
          {selectedShiftCount === 0 ? `Select up to ${MAX_BULK_SHIFT_EDITS} shifts.` : `${selectedShiftCount} shift${selectedShiftCount === 1 ? '' : 's'} selected.`}
        </span>
        {bulkActionMode === BULK_ACTION_MODES.EDIT_SHIFTS && (
          <span className={'bulk-shift-mode-hint'}>
            {'Unfilled, filled, and confirmed shifts are shown. Applicants or assigned Locums will be notified where applicable.'}
          </span>
        )}
        {bulkActionMode === BULK_ACTION_MODES.CHANGE_STORE && (
          <span className={'bulk-shift-mode-hint'}>
            {'Only filled/confirmed shifts are eligible for store change. Click a shift card to select it.'}
          </span>
        )}
      </section>
      <section className={'bulk-shift-buttons'}>
        {bulkActionMode === BULK_ACTION_MODES.EDIT_SHIFTS &&
          <BulkEditShiftButton
            selectedShifts={selectedShiftsOrdered}
            clearSelection={cancelBulkActionMode}
            storeId={storeId}
            disabled={actionDisabled}
            buttonText={`Bulk edit ${selectedShiftCount} selected shift${selectedShiftCount === 1 ? '' : 's'}`}
            setBulkStatusMessage={setBulkStatusMessage}
          />
        }
        {bulkActionMode === BULK_ACTION_MODES.CHANGE_STORE &&
          <button
            type={'button'}
            className={`bulk-change-store-shifts very-dark ${actionButtonClass}`}
            disabled={actionDisabled}
            onClick={() => openBulkStoreChange(selectedShiftsOrdered)}
          >
            {`Bulk change ${selectedShiftCount} selected shift${selectedShiftCount === 1 ? '' : 's'}`}
          </button>
        }
        <button type={'button'} className={'cancel-bulk-selection very-light'} onClick={cancelBulkActionMode}>
          {'Cancel'}
        </button>
      </section>
      {bulkStatusMessage && <section className={`bulk-shift-status ${bulkStatusMessage.type}`}>{bulkStatusMessage.text}</section>}
    </section>
  );
};

const getStoreTimeZone = (store) => store && store.address ? store.address.timeZone : null;

const getEligibleStoreChangeTargets = (stores, shift) => {
  return stores.filter(store => (
    store._id !== shift.storeId
  ));
};

const hasDifferentTimeZone = (currentStore, targetStore) => (
  currentStore
  && targetStore
  && getStoreTimeZone(currentStore)
  && getStoreTimeZone(targetStore)
  && getStoreTimeZone(currentStore) !== getStoreTimeZone(targetStore)
);

const CROSS_TIMEZONE_STORE_CHANGE_WARNING = 'This store is in a different timezone. The shift will keep the same local start and end time in the new store\'s timezone, so the Locum\'s calendar time may change.';

const getBulkEligibleStores = (stores, shifts) => {
  if (!shifts.length) {
    return [];
  }
  let eligible = getEligibleStoreChangeTargets(stores, shifts[0]);
  for (let i = 1; i < shifts.length; i++) {
    const allowedIds = new Set(getEligibleStoreChangeTargets(stores, shifts[i]).map((s) => s._id));
    eligible = eligible.filter((s) => allowedIds.has(s._id));
  }
  return eligible;
};

const getLocumSortName = (shift) => (
  shift.locum ? `${shift.locum.firstName || ''} ${shift.locum.lastName || ''}`.trim().toLowerCase() : ''
);

const sortShiftsByLocumAndDate = (shifts) => (
  [...shifts].sort((a, b) => {
    const locumCompare = getLocumSortName(a).localeCompare(getLocumSortName(b));
    if (locumCompare !== 0) {
      return locumCompare;
    }
    return a.startTime - b.startTime;
  })
);

const BulkChangeShiftStoreConfirmation = ({ shifts, stores, listStoreId, close, onChanged, setBulkStatusMessage = () => {} }) => {
  const { setShifts } = useStore();
  const [reason, setReason] = useState(null);
  const eligibleStores = getBulkEligibleStores(stores, shifts);
  const [newStoreId, setNewStoreId] = useState(eligibleStores.length > 0 ? eligibleStores[0]._id : null);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const sortedShifts = sortShiftsByLocumAndDate(shifts);
  const firstShift = shifts[0];
  const currentStore = firstShift ? stores.find((store) => store._id === firstShift.storeId) : null;
  const targetStore = stores.find((store) => store._id === newStoreId);
  const showTimezoneWarning = hasDifferentTimeZone(currentStore, targetStore);
  const disabled = reason === null || newStoreId === null || loading || !shifts.length;
  const onConfirm = () => {
    if (disabled) {
      return;
    }
    setLoading(true);
    setErrorText(null);
    const shiftIds = sortedShifts.map(({ _id }) => _id);
    postBulkShiftStoreChange(shiftIds, newStoreId, reason)
      .then((response) => {
        if (response.error) {
          setLoading(false);
          setErrorText(response.error);
          return;
        }
        getShiftsForStore(listStoreId)
          .then(({ shifts: nextShifts }) => {
            setShifts(nextShifts);
            setBulkStatusMessage({ type: 'success', text: response.message || `Successfully changed store for ${shiftIds.length} shift${shiftIds.length === 1 ? '' : 's'}.` });
            setLoading(false);
            onChanged();
            close();
          })
          .catch(() => {
            setBulkStatusMessage({ type: 'warning', text: 'Bulk store change succeeded, but shifts could not refresh. Please reload the page.' });
            setLoading(false);
            onChanged();
            close();
          });
      })
      .catch((err) => {
        setLoading(false);
        setErrorText(err.message || 'An error occurred! Please report this to the administrators.');
      });
  };
  return (
    <ConfirmationModal close={close} confirm={onConfirm} confirmText={'Change store for all'} disabled={disabled} additionalClassName={'change-store'} loading={loading}>
      <h3 className={'confirmation-title'}>{`Change store for ${shifts.length} shift${shifts.length === 1 ? '' : 's'}`}</h3>
      <p className={'confirmation-text'}>
        {'Each assigned Locum will be notified. Only destinations valid for every selected shift are listed.'}
      </p>
      <ul className={'shift-store-changes bulk-multi-shift-list'}>
        {sortedShifts.map((shiftRow) => {
          const locumName = shiftRow.locum ? `${shiftRow.locum.firstName} ${shiftRow.locum.lastName}` : 'Locum';
          return (
            <li className={'change-item bulk-multi-shift-row'} key={shiftRow._id}>
              <span className={'change-label'}>{`${locumName} · `}</span>
              <span className={'change-value'}>{new Date(shiftRow.startTime).toLocaleString()}</span>
            </li>
          );
        })}
      </ul>
      <ul className={'shift-store-changes'}>
        <li className={'change-item'}>
          <span className={'change-label'}>{'Current store: '}</span>
          <span className={'change-value'}>{currentStore ? currentStore.name : (firstShift ? firstShift.name : '')}</span>
        </li>
        <li className={'change-item'}>
          <span className={'change-label'}>{'New store: '}</span>
          <select className={'store-select'} value={newStoreId || ''} onChange={(e) => setNewStoreId(e.target.value)} disabled={eligibleStores.length === 0}>
            {eligibleStores.map((store) => (
              <option value={store._id} key={`bulk-change-store-${store._id}`}>{store.name}</option>
            ))}
          </select>
        </li>
      </ul>
      {targetStore && (
        <p className={'confirmation-text warning'}>
          {'Where a Locum has no provider number for the new store, that shift will require provider number confirmation again.'}
        </p>
      )}
      {showTimezoneWarning && (
        <p className={'confirmation-text warning'}>
          {CROSS_TIMEZONE_STORE_CHANGE_WARNING}
        </p>
      )}
      {eligibleStores.length === 0 &&
        <p className={'error-text'}>{'No single destination store works for all selected shifts. Deselect some shifts or change them individually.'}</p>
      }
      <section className={'reason-dropdown-container'}>
        <span className={'reason-text'}>{'Reason: '}</span>
        <ReasonDropdown setReason={setReason} reason={reason} options={STORE_CHANGE_OPTIONS} />
      </section>
      <section className={'error-text'}>{errorText}</section>
    </ConfirmationModal>
  );
};

const ChangeShiftStoreConfirmation = ({ shift, stores, close, onChanged, listStoreId }) => {
  const { setShifts } = useStore();
  const [reason, setReason] = useState(null);
  const eligibleStores = getEligibleStoreChangeTargets(stores, shift);
  const [newStoreId, setNewStoreId] = useState(eligibleStores.length > 0 ? eligibleStores[0]._id : null);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const currentStore = stores.find(store => store._id === shift.storeId);
  const targetStore = stores.find(store => store._id === newStoreId);
  const showTimezoneWarning = hasDifferentTimeZone(currentStore, targetStore);
  const locumName = shift.locum ? `${shift.locum.firstName} ${shift.locum.lastName}` : 'your Locum';
  const disabled = reason === null || newStoreId === null || loading;
  const refreshStoreId = listStoreId !== undefined && listStoreId !== null ? listStoreId : shift.storeId;
  const onConfirm = () => {
    if (disabled) {
      return;
    }
    setLoading(true);
    setErrorText(null);
    postShiftStoreChange(shift._id, newStoreId, reason)
      .then((response) => {
        if (response.error) {
          setLoading(false);
          setErrorText(response.error);
          return;
        }
        getShiftsForStore(refreshStoreId)
          .then(({ shifts }) => {
            setShifts(shifts);
            setLoading(false);
            onChanged();
            close();
          })
          .catch(() => {
            setLoading(false);
            setErrorText('Store changed, but shifts could not refresh. Please reload the page.');
          });
      })
      .catch((err) => {
        setLoading(false);
        setErrorText(err.message || 'An error occurred! Please report this to the administrators.');
      });
  };
  return (
    <ConfirmationModal close={close} confirm={onConfirm} confirmText={'Change store'} disabled={disabled} additionalClassName={'change-store'} loading={loading}>
      <h3 className={'confirmation-title'}>{'Change shift store'}</h3>
      <p className={'confirmation-text'}>
        {`This will move the shift for ${locumName} to another store. The Locum will be notified.`}
      </p>
      <ul className={'shift-store-changes'}>
        <li className={'change-item'}>
          <span className={'change-label'}>{'Current store: '}</span>
          <span className={'change-value'}>{currentStore ? currentStore.name : shift.name}</span>
        </li>
        <li className={'change-item'}>
          <span className={'change-label'}>{'New store: '}</span>
          <select className={'store-select'} value={newStoreId || ''} onChange={(e) => setNewStoreId(e.target.value)} disabled={eligibleStores.length === 0}>
            {eligibleStores.map(store => (
              <option value={store._id} key={`change-store-${store._id}`}>{store.name}</option>
            ))}
          </select>
        </li>
        <li className={'change-item'}>
          <span className={'change-label'}>{'Locum: '}</span>
          <span className={'change-value'}>{locumName}</span>
        </li>
      </ul>
      {targetStore &&
        <p className={'confirmation-text warning'}>
          {`If ${locumName} does not have a provider number for ${targetStore.name}, this shift will require provider number confirmation again.`}
        </p>
      }
      {showTimezoneWarning &&
        <p className={'confirmation-text warning'}>
          {CROSS_TIMEZONE_STORE_CHANGE_WARNING}
        </p>
      }
      {eligibleStores.length === 0 &&
        <p className={'error-text'}>{'No eligible stores are available.'}</p>
      }
      <section className={'reason-dropdown-container'}>
        <span className={'reason-text'}>{'Reason: '}</span>
        <ReasonDropdown setReason={setReason} reason={reason} options={STORE_CHANGE_OPTIONS} />
      </section>
      <section className={'error-text'}>{errorText}</section>
    </ConfirmationModal>
  );
};

export const ShiftListViewer = ({
  shifts,
  CardComponent,
  review,
  select,
  selectedId,
  emptyText,
  selectLocum,
  selectedShiftIds = [],
  toggleBulkSelect = null,
  isBulkSelectable = () => false,
  onChangeStore = null,
  canChangeStore = () => true,
  store = null,
  listStoreId = null,
  clearBulkSelection = null,
  upcomingCardActions = false,
  bulkSelectionMode = false,
}) => {
  if (shifts === null) {
    return (
      <section className={'shift-list-viewer'}>
        <ul className={'shift-list no-shifts'}>
          <span className={'no-shifts-text'}>{'Loading shifts...'}</span>
        </ul>
      </section>
    );
  }
  const shiftList = shifts
    .map(shift => {
      return <CardComponent
        shift={shift}
        authed={true}
        review={review}
        select={select}
        selectLocum={selectLocum}
        selectedId={selectedId}
        showBulkSelect={toggleBulkSelect !== null}
        bulkSelected={selectedShiftIds.includes(shift._id)}
        bulkSelectable={isBulkSelectable(shift)}
        toggleBulkSelect={toggleBulkSelect}
        bulkSelectionMode={bulkSelectionMode}
        onChangeStore={onChangeStore !== null && canChangeStore(shift) ? onChangeStore : null}
        store={store}
        listStoreId={listStoreId}
        clearBulkSelection={clearBulkSelection}
        upcomingCardActions={upcomingCardActions}
        key={`shift-${shift._id}`}
      />;
    });
  return (
    <section className={'shift-list-viewer'}>
      <ul className={`shift-list ${shiftList.length === 0 ? 'no-shifts' : ''}`}>
        {shiftList}
        {shiftList.length === 0 && <span className={'no-shifts-text'}>{emptyText}</span> }
      </ul>
    </section>
  );
};

export const ShiftViewer = ({ shift, select = () => {}, back = null, store, openChangeStore = null, canChangeStore = false, listStoreId = null }) => {
  const returnToShifts = () => select(null);
  const modifiable = isEditableShift(shift);
  const canEdit = isEditableShift(shift);
  const deleteListStoreId = listStoreId !== null && listStoreId !== undefined ? listStoreId : (store ? store._id : null);
  return (
    <section className={'view-shift-container'}>
      <section className={'button-containers'}>
        <BackArrowButton onClick={back === null ? returnToShifts : back} text={'Back to my shifts'} />
      { modifiable &&
        <section className={'shift-buttons'}>
          <EditShiftButton shift={shift} editText={'Edit shift'} select={select} classNames={['edit-shift', 'shift-button']} store={store} disabled={!canEdit} />
          {canChangeStore &&
            <button className={'change-store shift-button very-dark'} onClick={() => openChangeStore(shift)}>
              {'Change store'}
            </button>
          }
          <DeleteShiftButton returnToShifts={returnToShifts} shiftId={shift._id} listStoreId={deleteListStoreId} />
        </section>
      }
      </section>
      <Shift shift={shift} showRequestButton={false} isViewer={true} />
    </section>
  );
};

export const LocumViewer = ({ locum, confirm = null, backText, shift = null, back }) => {
  return (
    <section className={'view-locum-container'}>
      <BackArrowButton onClick={back} text={backText} />
      <LocumProfile locum={locum} confirm={confirm} shift={shift} />
    </section>
  )
}

const UpcomingTitleAndSubtitle = () => (
  <>
    <h1 className={'upcoming-shifts-title'}>{'Upcoming Shifts'}</h1>
    <p className={'upcoming-shifts-subtitle'}>
      {`Summary of upcoming shifts for your practice.`}
    </p>
    <p className={'upcoming-shifts-subtitle'}>
      {`Unfilled shifts are visible to Locums on the marketplace.`}
    </p>
  </>
);

const SelectedShiftTitleAndSubtitle = ({ unfilled }) => (
  <>
    <h1 className={'upcoming-shifts-title'}>{'Your Selected Shift'}</h1>
    <p className={'upcoming-shifts-subtitle'}>
      {`This is how your shift appears to Locums.`}
    </p>
    <p className={'upcoming-shifts-subtitle'}>

      {unfilled 
        ? `You can modify your shift details. Applicants will be informed of any changes.`
        : `You can modify your shift details. The assigned Locum will be informed of any changes.`
      }
    </p>
  </>
);

const SelectedLocumTitleAndSubtitle = () => {
  return (
    <>
      <h1 className={'upcoming-shifts-title'}>{'Your Selected Locum'}</h1>
      <p className={'upcoming-shifts-subtitle'}>{'This is your selected locum for this shift.'}</p>
      <p className={'upcoming-shifts-subtitle'}>{'Pro tip: You can message your Locum before the shift start with all the details they need to know to have a good shfit.'}</p>
    </>
  );
}

const UpcomingShiftsPage = () => {
  const { profile } = useAuth();
  const { shifts } = useStore();
  const [selectedShiftIds, setSelectedShiftIds] = useState([]);
  const [bulkActionMode, setBulkActionMode] = useState(null);
  const allShifts = shifts !== null
    ? shifts
        .filter(shift => shift.endTime > new Date().getTime())
        .sort((e1, e2) => e1.startTime - e2.startTime)
    : null;
  let { state } = useLocation();
  const { storeId } = state;
  const previousStoreId = useRef(storeId);
  const store = profile.stores.find((store) => store._id === storeId);
  const emptyText = 'No upcoming shifts';
  const [shift, setShift] = useState(state.shift ? state.shift : null);
  const [locum, setLocum] = useState(null);
  const [storeChangeShift, setStoreChangeShift] = useState(null);
  const [bulkStoreChangeShifts, setBulkStoreChangeShifts] = useState(null);
  const [bulkStatusMessage, setBulkStatusMessage] = useState(null);
  const clearSelection = () => setSelectedShiftIds([]);
  const startBulkActionMode = (mode) => {
    clearSelection();
    setBulkStatusMessage(null);
    setStoreChangeShift(null);
    setBulkStoreChangeShifts(null);
    setBulkActionMode(mode);
  };
  const cancelBulkActionMode = () => {
    clearSelection();
    setBulkActionMode(null);
  };
  const canChangeStore = (s) => (
    s !== null
    && profile.stores.length > 1
    && (s.status === SHIFT_STATUSES.FILLED.key || s.status === SHIFT_STATUSES.CONFIRMED.key)
    && s.startTime > Date.now()
    && !s.invoiceLink
    && !s.paymentLink
    && getEligibleStoreChangeTargets(profile.stores, s).length > 0
  );
  const openChangeStore = (shiftToChange) => {
    clearSelection();
    setBulkStatusMessage(null);
    setBulkActionMode(null);
    setBulkStoreChangeShifts(null);
    setStoreChangeShift(shiftToChange);
  };
  const closeChangeStore = () => setStoreChangeShift(null);
  const closeBulkStoreChange = () => setBulkStoreChangeShifts(null);
  const onStoreChanged = () => {
    setShift(null);
    setLocum(null);
    cancelBulkActionMode();
  };
  const selectedShiftsOrdered = allShifts === null
    ? []
    : selectedShiftIds.map((id) => allShifts.find((row) => row._id === id)).filter(Boolean);
  const displayedShifts = allShifts === null
    ? null
    : allShifts.filter((row) => {
        if (bulkActionMode === BULK_ACTION_MODES.EDIT_SHIFTS) {
          return isEditableShift(row);
        }
        if (bulkActionMode === BULK_ACTION_MODES.CHANGE_STORE) {
          return canChangeStore(row);
        }
        return true;
      });
  const bulkEmptyText = bulkActionMode === BULK_ACTION_MODES.EDIT_SHIFTS
    ? 'No shifts available to edit'
    : 'No shifts available for bulk store change';
  const displayedEmptyText = bulkActionMode === null ? emptyText : bulkEmptyText;
  const isBulkSelectable = (row) => {
    if (allShifts === null || bulkActionMode === null) {
      return false;
    }
    const inSelection = selectedShiftIds.includes(row._id);
    if (!inSelection && selectedShiftIds.length >= MAX_BULK_SHIFT_EDITS) {
      return false;
    }
    if (bulkActionMode === BULK_ACTION_MODES.EDIT_SHIFTS) {
      return isEditableShift(row);
    }
    if (bulkActionMode === BULK_ACTION_MODES.CHANGE_STORE) {
      return canChangeStore(row);
    }
    return false;
  };
  const toggleBulkSelect = (selectedShift) => {
    if (!isBulkSelectable(selectedShift)) {
      return;
    }
    setSelectedShiftIds((currentIds) => (
      currentIds.includes(selectedShift._id)
        ? currentIds.filter(id => id !== selectedShift._id)
        : [...currentIds, selectedShift._id]
    ));
  };
  const openBulkStoreChange = (batch) => {
    setStoreChangeShift(null);
    setBulkStoreChangeShifts(batch);
  };
  const selectShift = (s) => {
    cancelBulkActionMode();
    setShift(s);
  };
  const selectLocum = (locumRow, shiftRow) => {
    cancelBulkActionMode();
    setLocum(locumRow);
    setShift(shiftRow);
  }
  const back = () => {
    cancelBulkActionMode();
    setLocum(null);
    setShift(null);
  }
  const onViewingStoreChange = useCallback(() => {
    clearSelection();
    setBulkActionMode(null);
    setBulkStatusMessage(null);
    setStoreChangeShift(null);
    setBulkStoreChangeShifts(null);
    setLocum(null);
    setShift(null);
  }, []);
  useEffect(() => {
    if (previousStoreId.current !== storeId) {
      onViewingStoreChange();
      previousStoreId.current = storeId;
    }
  }, [storeId, onViewingStoreChange]);
  const unfilled = shift !== null && shift.status === SHIFT_STATUSES.UNFILLED.key;
  return (
    <StorePage mode={NAVIGATION_OPTIONS.UPCOMING.mode} onStoreChange={onViewingStoreChange}>
      <article className={'upcoming-shifts-page'}>
        {shift === null && locum === null && <UpcomingTitleAndSubtitle />}
        {shift !== null && locum === null && <SelectedShiftTitleAndSubtitle unfilled={unfilled} /> }
        {shift !== null && locum !== null && <SelectedLocumTitleAndSubtitle unfilled={unfilled} /> }
        {shift === null && locum === null &&
          <section className={'shift-viewer-container'}>
            {allShifts !== null && (allShifts.length > 0 || bulkActionMode !== null) &&
              <BulkShiftActions
                selectedShiftsOrdered={selectedShiftsOrdered}
                bulkActionMode={bulkActionMode}
                startBulkActionMode={startBulkActionMode}
                cancelBulkActionMode={cancelBulkActionMode}
                storeId={storeId}
                openBulkStoreChange={openBulkStoreChange}
                bulkStatusMessage={bulkStatusMessage}
                setBulkStatusMessage={setBulkStatusMessage}
              />
            }
            <ShiftListViewer
              shifts={displayedShifts}
              CardComponent={BusinessShiftCard}
              emptyText={displayedEmptyText}
              select={selectShift}
              selectLocum={selectLocum}
              selectedShiftIds={selectedShiftIds}
              toggleBulkSelect={bulkActionMode === null ? null : toggleBulkSelect}
              isBulkSelectable={isBulkSelectable}
              onChangeStore={openChangeStore}
              canChangeStore={canChangeStore}
              store={store}
              listStoreId={storeId}
              clearBulkSelection={clearSelection}
              upcomingCardActions={true}
              bulkSelectionMode={bulkActionMode !== null}
            />
          </section>
        }
        {shift !== null && locum === null &&
          <ShiftViewer
            shift={shift}
            select={setShift}
            store={store}
            openChangeStore={openChangeStore}
            canChangeStore={canChangeStore(shift)}
            listStoreId={storeId}
          />
        }
        {locum !== null && shift !== null && <LocumViewer locum={locum} selectLocum={selectLocum} shift={shift} backText={'Back to my shifts'} back={back} />}
        {storeChangeShift !== null &&
          <ChangeShiftStoreConfirmation
            key={storeChangeShift._id}
            shift={storeChangeShift}
            stores={profile.stores}
            listStoreId={storeId}
            close={closeChangeStore}
            onChanged={onStoreChanged}
          />
        }
        {bulkStoreChangeShifts !== null && bulkStoreChangeShifts.length > 0 &&
          <BulkChangeShiftStoreConfirmation
            key={bulkStoreChangeShifts.map((s) => s._id).join('-')}
            shifts={bulkStoreChangeShifts}
            stores={profile.stores}
            listStoreId={storeId}
            close={closeBulkStoreChange}
            onChanged={onStoreChanged}
            setBulkStatusMessage={setBulkStatusMessage}
          />
        }
      </article>
    </StorePage>
  );
}

export default UpcomingShiftsPage;

export { ReasonDropdown } from '../common/ReasonDropdown.jsx';
export { EditShiftButton, getTimeOffset } from './EditShiftButton.jsx';
